import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  PackageStructureResponseType,
  PackageStructureWimsItemType,
  PackageStructureWimsResponseType,
} from 'features/Product/services/packageStructure'
import { ProductV1Type, GetUomResponseType } from 'features/Product/@types/Product'

export type UomItemType = Pick<
  GetUomResponseType['unit_of_measurements']['content'][number],
  'id' | 'description'
> & {
  name: string
}

export const PACKAGE_STRUCTURE_TYPE = [
  {
    value: 'custom',
    name: 'Custom',
  },
  {
    value: 'inner-outer',
    name: 'Inner & Outer',
  },
]
export type PackageStructureListType = typeof PACKAGE_STRUCTURE_TYPE

export type TypeOfPackageStructureType = 'custom' | 'inner' | 'outer'

export type SummaryInnerOuterItemDataType = {
  fromUomDescription: string
  useRange: boolean
  qty: number
  minQty: number
  maxQty: number
  toUomDescription: string
}

export type PackageStructureInitialStateType = {
  isLoading: boolean
  productId: number
  selectedProduct: Nullable<ProductV1Type>
  params: {
    pageIndex: number
    pageSize: number
    productId: number
  }
  pagination: PackageStructureResponseType['pagination']
  queryParams: {
    page_index: number
    page_size: number
  }
  paginationData: PackageStructureResponseType['pagination']
  modalAddEdit: {
    id: {
      custom: number
      inner: number
      outer: number
    }
    isOpen: boolean
    type: 'custom' | 'inner' | 'outer'
    isInnerFormEdit: boolean
    isOuterFormEdit: boolean
    modalType: string
    uomList: UomItemType[]
    selectedPackageType: Nullable<PackageStructureListType[number]>
    barcodeOfInner: string
    selectedInnerUom: Nullable<UomItemType>
    isDisabledBtnSubmit: {
      custom: boolean
      inner: boolean
      outer: boolean
    }
  }
  modalDelete: {
    isOpen: boolean
    type: string
    id: number[]
    isDisabledBtnSubmit: boolean
    message: string
  }
  packageStructureList: PackageStructureResponseType['data']
  packageStructureWimsList: PackageStructureWimsResponseType['data']
  summaryInformation: {
    product_image: string
    product_name: string
    product_sku_no: string
    product_categories: {
      id: number
      name: string
      is_primary: boolean
    }[]
    uom: {
      group: string
      description: string
      active: boolean
      id: string
    }
  }
  packageStructureDetail: {
    [key: string]: Nullable<PackageStructureWimsItemType>
  }
  barcodeNumberOfBaseType: string
  baseUomTo: UomItemType
  isInnerOrOuterTypeExist: boolean
  summaryInnerData: Nullable<SummaryInnerOuterItemDataType>
  summaryOuterData: Nullable<SummaryInnerOuterItemDataType>
  triggerReloadFormOuter: boolean
}

export const initialState: PackageStructureInitialStateType = {
  isLoading: false,
  productId: 0,
  selectedProduct: null,
  params: {
    pageIndex: 1,
    pageSize: 20,
    productId: 0,
  },
  pagination: {
    direction: 'asc',
    page_size: 20,
    number_of_elements: 0,
    sort: '',
    page_index: 1,
  },
  queryParams: {
    page_index: 1,
    page_size: 20,
  },
  paginationData: {
    direction: '',
    page_index: 1,
    page_size: 20,
    number_of_elements: 0,
    sort: '',
  },
  modalAddEdit: {
    id: {
      custom: 0,
      inner: 0,
      outer: 0,
    },
    isOpen: false,
    type: 'custom',
    isInnerFormEdit: false,
    isOuterFormEdit: false,
    modalType: '',
    uomList: [],
    selectedPackageType: null,
    barcodeOfInner: '',
    selectedInnerUom: null,
    isDisabledBtnSubmit: {
      custom: false,
      inner: false,
      outer: false,
    },
  },
  modalDelete: {
    isOpen: false,
    type: '',
    id: [],
    isDisabledBtnSubmit: false,
    message: '',
  },
  packageStructureList: [],
  packageStructureWimsList: [],
  summaryInformation: {
    product_image: '',
    product_name: '',
    product_sku_no: '',
    product_categories: [],
    uom: {
      group: '',
      description: '',
      active: false,
      id: '',
    },
  },
  packageStructureDetail: {
    custom: null,
    inner: null,
    outer: null,
  },
  barcodeNumberOfBaseType: '',
  baseUomTo: {
    id: '',
    description: '',
    name: '',
  },
  isInnerOrOuterTypeExist: false,
  summaryInnerData: null,
  summaryOuterData: null,
  triggerReloadFormOuter: false,
}

const packageStructureSlice = createSlice({
  name: 'packageStructure',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setProductId: (state, action: PayloadAction<number>) => {
      state.productId = action.payload
    },
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload
    },
    setParamsPackageStructure: (
      state,
      action: PayloadAction<Partial<PackageStructureInitialStateType['params']>>,
    ) => {
      state.params = {
        ...state.params,
        ...action.payload,
      }
    },
    setQueryParams: (state, action) => {
      state.queryParams = {
        ...state.queryParams,
        ...action.payload,
      }
    },
    setPackageStructureList: (
      state,
      action: PayloadAction<{
        data: PackageStructureResponseType['data']
        pagination: PackageStructureInitialStateType['pagination']
      }>,
    ) => {
      const { data, pagination } = action.payload
      state.packageStructureList = data
      state.pagination = pagination
    },
    setPackageStructureOnFetchSucceed: (
      state,
      action: PayloadAction<{
        data: PackageStructureWimsResponseType['data']
        pagination: PackageStructureInitialStateType['pagination']
      }>,
    ) => {
      const { data, pagination } = action.payload
      state.packageStructureWimsList = data
      state.paginationData = pagination
    },
    setSummaryInformation: (
      state,
      action: PayloadAction<PackageStructureInitialStateType['summaryInformation']>,
    ) => {
      state.summaryInformation = action.payload
    },
    setPackageStructureDetail: (
      state,
      action: PayloadAction<PackageStructureInitialStateType['packageStructureDetail']>,
    ) => {
      state.packageStructureDetail = {
        ...state.packageStructureDetail,
        ...action.payload,
      }
    },
    resetPackageStructureDetail: (state) => {
      state.packageStructureDetail = initialState.packageStructureDetail
    },
    setBarcodeNumberOfBaseType: (state, action: PayloadAction<string>) => {
      state.barcodeNumberOfBaseType = action.payload
    },
    setBaseUomTo: (state, action: PayloadAction<UomItemType>) => {
      state.baseUomTo = action.payload
    },
    setModalAddEdit: (state, action) => {
      state.modalAddEdit = {
        ...state.modalAddEdit,
        ...action.payload,
      }
    },
    setIdOfModalAddEdit: (
      state,
      action: PayloadAction<{
        field: keyof PackageStructureInitialStateType['modalAddEdit']['id']
        id: number
      }>,
    ) => {
      const { field, id } = action.payload
      state.modalAddEdit.id = {
        ...state.modalAddEdit.id,
        [field]: id,
      }
    },
    setIsDisabledBtnSubmitModalAddEdit: (
      state,
      action: PayloadAction<
        Partial<PackageStructureInitialStateType['modalAddEdit']['isDisabledBtnSubmit']>
      >,
    ) => {
      state.modalAddEdit.isDisabledBtnSubmit = {
        ...state.modalAddEdit.isDisabledBtnSubmit,
        ...action.payload,
      }
    },
    resetModalAddEdit: (state) => {
      state.modalAddEdit = initialState.modalAddEdit
    },
    setModalDelete: (state, action) => {
      state.modalDelete = {
        ...state.modalDelete,
        ...action.payload,
      }
    },
    resetModalDelete: (state) => {
      state.modalDelete = initialState.modalDelete
    },
    setIsInnerOrOuterTypeExist: (state, action) => {
      state.isInnerOrOuterTypeExist = action.payload
    },
    setSummaryInnerOuterData: (
      state,
      action: PayloadAction<{
        type: 'summaryInnerData' | 'summaryOuterData'
        data: SummaryInnerOuterItemDataType
      }>,
    ) => {
      const { type, data } = action.payload
      state[type] = data
    },
    setToUomOfSummaryInnerOuterData: (
      state,
      action: PayloadAction<{
        type: 'summaryInnerData' | 'summaryOuterData'
        uom: string
      }>,
    ) => {
      const { type, uom } = action.payload
      const currentData = state[type]
      if (currentData) {
        state[type] = {
          ...currentData,
          toUomDescription: uom,
        }
      }
    },
    resetSummaryInnerOuterData: (state) => {
      state.summaryInnerData = initialState.summaryInnerData
      state.summaryOuterData = initialState.summaryOuterData
    },
    setTriggerReloadFormOuter: (state, action: PayloadAction<boolean>) => {
      state.triggerReloadFormOuter = action.payload
    },
  },
})

export const {
  setIsLoading,
  setProductId,
  setSelectedProduct,
  setParamsPackageStructure,
  setQueryParams,
  setPackageStructureList,
  setPackageStructureOnFetchSucceed,
  setSummaryInformation,
  setPackageStructureDetail,
  resetPackageStructureDetail,
  setBarcodeNumberOfBaseType,
  setBaseUomTo,
  setModalAddEdit,
  setIdOfModalAddEdit,
  setIsDisabledBtnSubmitModalAddEdit,
  resetModalAddEdit,
  setModalDelete,
  resetModalDelete,
  setIsInnerOrOuterTypeExist,
  setSummaryInnerOuterData,
  setToUomOfSummaryInnerOuterData,
  resetSummaryInnerOuterData,
  setTriggerReloadFormOuter,
} = packageStructureSlice.actions

export default packageStructureSlice.reducer
